/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { updateMPSBusinessProfile } from "../../../firebaseFunctions/updateMPSBusinessProfile/updateMPSBusinessProfile";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../../../firebaseFunctions/config";
import ModalLoadingState from "../../PopUpModal/ModalLoadingState";
import FormField from "../../ui/FormField";
import { PhoneMissed } from "lucide-react";
import picturePlaceHolder from "../../../assets/images/picture_placeholder.svg";
import { validateAndResizeProfilePhoto } from "../../../utils/imageUtils";

const isValidPhoneNumber = (phoneNumber) => {
  const phoneRegex = /^(\+1|1)?[-.\s]?\(?[2-9]\d{2}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
  return phoneRegex.test(phoneNumber);
};

const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const ProfileContent = ({
  profileData,
  user,
  userSignInMethod,
  isLoading,
  setIsLoading,
  reload,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState({});

  const businessTypeOptions = [
    "Artisan / Crafted Goods",
    "Beverage – Non-Alcoholic",
    "Beverage – Alcoholic Bar",
    "Beverage – Craft Alcohol Sales",
    "Children's Goods",
    "Clothing",
    "Educational / Services",
    "Food - Prepared",
    "Food - Service",
    "Food Truck",
    "Health, Wellness & Beauty",
    "Meats & Produce",
    "Flowers & Plants",
  ];

  const initialState = {
    companyLogo: profileData?.companyLogo || null,
    businessName: profileData?.businessName || "",
    fullName: profileData?.fullName || "",
    email: profileData?.email || "",
    businessEmail: profileData?.businessEmail || "",
    phoneNumber: profileData?.phoneNumber || "",
    SMScontact: profileData?.SMScontact || "",
    description: profileData?.description || "",
    typeOfBusiness: profileData?.typeOfBusiness || "",
  };
  const [formState, setFormState] = useState({});
  useEffect(() => {
    if (profileData) {
      setFormState(initialState);
    }
  }, [profileData]); // eslint-disable-line react-hooks/exhaustive-deps

  const formFields = [
    {
      label: "Full Name",
      name: "fullName",
      value: formState.fullName,
      placeholder: "Your full name",
      type: "text",
      error: null,
    },
    {
      label: "Business Type",
      name: "typeOfBusiness",
      value: formState.typeOfBusiness,
      placeholder: "e.g. Handmade Goods, Food & Beverage",
      type: "text",
      error: null,
    },
    {
      label: "Business Phone Number",
      name: "phoneNumber",
      value: formState.phoneNumber,
      placeholder: "+1 234 567 8910",
      type: "text",
    },
    {
      label: "Email Address",
      name: "email",
      value: formState.email,
      placeholder: "example@email.com",
      type: "email",
    },
    {
      label: "SMS Contact Number",
      name: "SMScontact",
      value: formState.SMScontact,
      placeholder: "+1 234 567 8910",
      type: "text",
      error: null,
    },
  ];
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await updateMPSBusinessProfile(user.uid, formState);
      reload();
      setFormState(initialState);
      setIsEditing(false);
      setErrors({});
    } catch (error) {
      console.error("Error saving profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormState((prev) => {
      if (field === "email" && userSignInMethod === "Google") {
        return prev;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleLogoUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (!file) return;

      setIsLoading(true);

      // Process and validate the image
      const processedFile = await validateAndResizeProfilePhoto(file);

      // Upload to Firebase Storage first
      const storage = getStorage(app);
      const timestamp = new Date().toISOString();
      const fileName = `${processedFile.name}_${timestamp}`;
      const logoPath = `MPS_companyLogos/${fileName}`;
      const fileRef = ref(storage, logoPath);

      // Upload the file
      const uploadTaskSnapshot = await uploadBytes(fileRef, processedFile);

      // Get the download URL
      const downloadURL = await getDownloadURL(uploadTaskSnapshot.ref);

      // Update form state with the new logo URL
      const updatedFormState = {
        ...formState,
        companyLogo: downloadURL,
        companyLogoError: null,
      };

      // Update the state
      setFormState(updatedFormState);

      // Save to profile
      await updateMPSBusinessProfile(user.uid, updatedFormState);

      // Reload the profile data
      await reload();
    } catch (error) {
      console.error("Error uploading logo:", error);
      setFormState((prev) => ({
        ...prev,
        companyLogoError: "Failed to upload image. Please try again.",
      }));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <ModalLoadingState />;

  if (!profileData) {
    return (
      <div className="w-full max-w-[800px] bg-white flex flex-col items-center justify-center p-8 gap-4">
        <p className="text-grey-600">
          No profile data found. Please complete your profile setup.
        </p>
        <button
          className="cursor-pointer py-2.5 px-[18px] bg-[#344054] rounded-lg shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] flex flex-row items-center justify-center"
          onClick={reload}
        >
          <span className="text-base leading-6 font-medium text-white">
            Refresh
          </span>
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="w-full max-w-[800px] bg-white flex flex-col items-start justify-start gap-8 text-base ">
        <div className="w-[183px] flex flex-col items-start justify-start">
          <div className="flex flex-col items-start justify-start gap-1.5">
            <div className="flex flex-row items-start justify-start">
              <div className="flex flex-row items-center justify-start">
                <div className="relative leading-5 font-medium">
                  Company Logo
                </div>
              </div>
            </div>
            <img
              className="rounded-lg h-28 w-28 object-cover"
              src={
                formState.companyLogo instanceof File
                  ? URL.createObjectURL(formState.companyLogo)
                  : formState.companyLogo || picturePlaceHolder
              }
              alt="Logo Preview"
            />
            <label
              htmlFor="company-logo-upload"
              className="flex items-center text-sm text-grey-900 gap-2 cursor-pointer"
            >
              <ion-icon name="image" />
              Upload Company Logo
            </label>
            <input
              id="company-logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
            />
            {formState.companyLogoError && (
              <div className="text-red-500 text-xs mt-1">
                {formState.companyLogoError}
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 w-full lg:w-3/4 mt-4">
          {formFields.map((field, index) => (
            <FormField
              key={index}
              field={field}
              isEdit={isEditing}
              handleFieldChange={handleFieldChange}
            />
          ))}
        </div>
        {isEditing && userSignInMethod === "Google" && (
          <div className="text-red-500 text-xs mt-1">
            You cannot edit the email because you signed in with Google.
          </div>
        )}
        <div className="w-full pt-6">
          {!isEditing && (
            <div className="w-3/4 border-t border-gray-300 my-4" />
          )}
          <FormField
            isEdit={isEditing}
            isMultiple={true}
            handleFieldChange={handleFieldChange}
            field={{
              label: isEditing ? "Business Description" : "About",
              name: "description",
              value: formState?.description,
              placeholder: "Write something about your event company here",
              type: "text",
            }}
          />
          {!isEditing && (
            <div className="w-3/4 border-t border-gray-300 my-4" />
          )}
        </div>
      </div>
      <div className="self-stretch flex flex-row items-start justify-end gap-[26px]">
        {isEditing ? (
          <>
            <button
              className="cursor-pointer [border:none] py-2.5 px-[18px] bg-white rounded-lg shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] box-border h-11 flex flex-row items-center justify-center border-[1px] border-solid border-[#d0d5dd]"
              onClick={() => {
                setFormState(initialState);
                setIsEditing(false);
              }}
            >
              <div className="relative text-base leading-6 font-medium font-inter text-[#344054] text-left">
                Cancel
              </div>
            </button>
            <button
              className="cursor-pointer [border:none] py-2.5 px-[18px] bg-[#344054] rounded-lg shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] flex flex-row items-center justify-center"
              onClick={handleSave}
            >
              <div className="relative text-base leading-6 font-medium font-inter text-white text-left">
                Save Changes
              </div>
            </button>
          </>
        ) : (
          <button
            className="cursor-pointer [border:none] py-2.5 px-[18px] bg-[#344054] rounded-lg shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] flex flex-row items-center justify-center"
            onClick={handleEdit}
          >
            <div className="relative text-base leading-6 font-medium font-inter text-white text-left">
              Edit My Profile
            </div>
          </button>
        )}
      </div>
    </>
  );
};

export default ProfileContent;
